import { useState, useRef, useEffect } from "react";
import { mockClients } from "../../../mocks/clients";

const statusOptions = ["Tümü", "Yeni Başvuru", "Ödeme Bekliyor", "Planlandı", "Tamamlandı", "Gecikti"];

const statusStyle: Record<string, { bg: string; color: string }> = {
  "Yeni Başvuru": { bg: "rgba(212,175,55,0.22)", color: "#fde68a" },
  "Ödeme Bekliyor": { bg: "rgba(249,115,22,0.22)", color: "#fdba74" },
  Planlandı: { bg: "rgba(168,85,247,0.22)", color: "#d8b4fe" },
  Tamamlandı: { bg: "rgba(16,185,129,0.22)", color: "#6ee7b7" },
  Gecikti: { bg: "rgba(239,68,68,0.22)", color: "#fca5a5" },
};

const typeStyle: Record<string, { bg: string; color: string; icon: string }> = {
  Sesli: { bg: "rgba(168,85,247,0.22)", color: "#d8b4fe", icon: "ri-mic-line" },
  Yazılı: { bg: "rgba(249,115,22,0.22)", color: "#fdba74", icon: "ri-file-text-line" },
  Günlük: { bg: "rgba(212,175,55,0.22)", color: "#fde68a", icon: "ri-sun-line" },
};

const consultationTypes = [
  { value: "Yazılı", label: "Yazılı", icon: "ri-file-text-line", color: "#fdba74" },
  { value: "Sesli", label: "Sesli", icon: "ri-mic-line", color: "#d8b4fe" },
  { value: "Günlük", label: "Günlük (Horary)", icon: "ri-sun-line", color: "#fde68a" },
];

const serviceList = [
  "Doğum Haritası", "Sinastri", "Solar Return", "İlişki Analizi",
  "Geçmiş Yaşam ve Karmalar", "Yıllık Öngörü", "Rektifikasyon",
  "Eleksiyon", "Astrocartography", "Horary",
];

const statusList = ["Yeni Başvuru", "Ödeme Bekliyor", "Planlandı"];

function DropdownSelect({
  value, onChange, options, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm cursor-pointer"
        style={{
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          color: value ? "#fff" : "rgba(255,255,255,0.4)",
        }}
      >
        <span>{value || placeholder || "Seçin..."}</span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line`} style={{ color: "rgba(255,255,255,0.5)" }} />
      </button>
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl overflow-auto max-h-44"
          style={{ background: "#221d50", border: "1px solid rgba(168,85,247,0.45)" }}
        >
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap transition-colors"
              style={{ color: value === opt ? "#d8b4fe" : "rgba(255,255,255,0.85)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(168,85,247,0.15)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TypeDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = consultationTypes.find((t) => t.value === value) || consultationTypes[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm cursor-pointer text-white"
        style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
      >
        <span className="flex items-center gap-2">
          <i className={`${selected.icon}`} style={{ color: selected.color }} />
          {selected.label}
        </span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line`} style={{ color: "rgba(255,255,255,0.5)" }} />
      </button>
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden"
          style={{ background: "#221d50", border: "1px solid rgba(168,85,247,0.45)" }}
        >
          {consultationTypes.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => { onChange(t.value); setOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer transition-colors whitespace-nowrap"
              style={{ color: value === t.value ? t.color : "rgba(255,255,255,0.85)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(168,85,247,0.15)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <i className={t.icon} style={{ color: t.color }} />
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyForm = {
  name: "", phone: "", service: "", consultationType: "Yazılı",
  startDate: "", deliveryDate: "", status: "Yeni Başvuru", notes: "",
};

export default function ClientManagement() {
  const [filterStatus, setFilterStatus] = useState("Tümü");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "card">("table");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saved, setSaved] = useState(false);

  const filtered = mockClients.filter((c) => {
    const matchStatus = filterStatus === "Tümü" || c.status === filterStatus;
    const matchSearch =
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.service.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            ✦ Müşteri Yönetimi
          </h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
            {mockClients.length} kayıtlı müşteri
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setForm(emptyForm); setShowModal(true); setSaved(false); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff" }}
          >
            <i className="ri-user-add-line" />
            Yeni Müşteri
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "rgba(255,255,255,0.5)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Müşteri veya hizmet ara..."
            className="pl-9 pr-4 py-2 rounded-xl text-sm text-white outline-none w-56"
            style={{
              background: "rgba(255,255,255,0.09)",
              border: "1px solid rgba(255,255,255,0.18)",
              fontFamily: "'Inter', sans-serif",
            }}
          />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)" }}>
          {statusOptions.map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
              style={
                filterStatus === s
                  ? { background: "rgba(212,175,55,0.25)", color: "#fde68a", border: "1px solid rgba(212,175,55,0.45)" }
                  : { color: "rgba(255,255,255,0.6)", border: "1px solid transparent" }
              }
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1 p-1 rounded-lg" style={{ background: "rgba(255,255,255,0.07)" }}>
          {(["table", "card"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              className="w-8 h-7 rounded-md flex items-center justify-center cursor-pointer transition-all"
              style={viewMode === m ? { background: "rgba(168,85,247,0.35)", color: "#d8b4fe" } : { color: "rgba(255,255,255,0.55)" }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={m === "table" ? "ri-table-line text-sm" : "ri-layout-grid-line text-sm"} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Table View */}
      {viewMode === "table" ? (
        <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.14)" }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.08)" }}>
                  {["Müşteri", "Telefon", "Hizmet", "Tür", "Başlangıç", "Teslim", "Durum", ""].map((h) => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-medium whitespace-nowrap"
                      style={{ color: "rgba(255,255,255,0.65)", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((client, idx) => {
                  const ts = typeStyle[client.consultationType] || typeStyle["Yazılı"];
                  const ss = statusStyle[client.status] || { bg: "rgba(255,255,255,0.1)", color: "#fff" };
                  return (
                    <tr key={client.id}
                      className="transition-colors hover:bg-white/[0.05]"
                      style={{ borderBottom: idx < filtered.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.5), rgba(212,175,55,0.4))", color: "#fff" }}>
                            {client.avatar}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white whitespace-nowrap">{client.name}</p>
                            {client.notes && (
                              <p className="text-xs truncate max-w-[140px]" style={{ color: "rgba(255,255,255,0.55)" }}>
                                {client.notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs whitespace-nowrap" style={{ color: "rgba(255,255,255,0.65)", fontFamily: "monospace" }}>
                          {client.phone}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm whitespace-nowrap font-medium" style={{ color: "rgba(255,255,255,0.85)" }}>
                          {client.service}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                          style={{ background: ts.bg, color: ts.color }}>
                          <i className={`${ts.icon} text-xs`} />
                          {client.consultationType}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs whitespace-nowrap" style={{ color: "rgba(255,255,255,0.6)" }}>
                          {client.startDate}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs whitespace-nowrap font-medium" style={{ color: client.status === "Gecikti" ? "#fca5a5" : "rgba(255,255,255,0.65)" }}>
                          {client.deliveryDate}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                          style={{ background: ss.bg, color: ss.color }}>
                          {client.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-all hover:bg-white/10"
                          style={{ color: "rgba(255,255,255,0.55)" }}>
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-more-2-line text-sm" />
                          </div>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-user-search-line text-3xl" style={{ color: "rgba(255,255,255,0.25)" }} />
              <p className="text-sm mt-3" style={{ color: "rgba(255,255,255,0.45)" }}>Sonuç bulunamadı</p>
            </div>
          )}
        </div>
      ) : (
        /* Card View */
        <div className="grid grid-cols-3 gap-4">
          {filtered.map((client) => {
            const ts = typeStyle[client.consultationType] || typeStyle["Yazılı"];
            const ss = statusStyle[client.status] || { bg: "rgba(255,255,255,0.1)", color: "#fff" };
            return (
              <div key={client.id} className="rounded-2xl p-5 transition-transform hover:-translate-y-0.5 cursor-pointer"
                style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)" }}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.5), rgba(212,175,55,0.4))", color: "#fff" }}>
                      {client.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{client.name}</p>
                      <p className="text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>{client.phone}</p>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ background: ss.bg, color: ss.color }}>
                    {client.status}
                  </span>
                </div>
                <div className="space-y-2 mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>Hizmet</span>
                    <span className="text-xs font-medium" style={{ color: "rgba(255,255,255,0.9)" }}>{client.service}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>Tür</span>
                    <span className="inline-flex items-center gap-1 text-xs" style={{ color: ts.color }}>
                      <i className={`${ts.icon} text-xs`} /> {client.consultationType}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>Teslim</span>
                    <span className="text-xs font-medium" style={{ color: client.status === "Gecikti" ? "#fca5a5" : "rgba(255,255,255,0.8)" }}>
                      {client.deliveryDate}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* NEW CLIENT MODAL */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.65)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div
            className="w-full max-w-lg rounded-2xl overflow-hidden"
            style={{ background: "#1e1840", border: "1px solid rgba(212,175,55,0.3)" }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", background: "rgba(168,85,247,0.1)" }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #a855f7, #d4af37)" }}>
                  <i className="ri-user-add-line text-white text-sm" />
                </div>
                <h2 className="font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Yeni Müşteri Ekle
                </h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-4">
              {saved ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
                    style={{ background: "rgba(16,185,129,0.2)", border: "2px solid rgba(16,185,129,0.4)" }}>
                    <i className="ri-check-line text-2xl" style={{ color: "#34d399" }} />
                  </div>
                  <p className="text-white font-semibold">Müşteri eklendi!</p>
                  <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>
                    {form.name} başarıyla kaydedildi.
                  </p>
                  <button
                    onClick={() => setShowModal(false)}
                    className="mt-4 px-6 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap"
                    style={{ background: "rgba(16,185,129,0.2)", color: "#34d399", border: "1px solid rgba(16,185,129,0.35)" }}
                  >
                    Tamam
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Ad Soyad *
                      </label>
                      <input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Örn: Ayşe Yılmaz"
                        className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none placeholder:text-white/30"
                        style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Telefon
                      </label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+90 5xx xxx xxxx"
                        className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none placeholder:text-white/30"
                        style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                      Hizmet *
                    </label>
                    <DropdownSelect
                      value={form.service}
                      onChange={(v) => setForm({ ...form, service: v })}
                      options={serviceList}
                      placeholder="Hizmet seçin..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                      Danışmanlık Türü
                    </label>
                    <TypeDropdown
                      value={form.consultationType}
                      onChange={(v) => setForm({ ...form, consultationType: v })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Başlangıç Tarihi
                      </label>
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none"
                        style={{
                          background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.15)",
                          colorScheme: "dark",
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Teslim Tarihi
                      </label>
                      <input
                        type="date"
                        value={form.deliveryDate}
                        onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })}
                        className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none"
                        style={{
                          background: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.15)",
                          colorScheme: "dark",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                      Durum
                    </label>
                    <DropdownSelect
                      value={form.status}
                      onChange={(v) => setForm({ ...form, status: v })}
                      options={statusList}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                      Notlar
                    </label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Müşteri hakkında not ekleyin..."
                      rows={2}
                      maxLength={500}
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none resize-none placeholder:text-white/30"
                      style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)" }}
                    />
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={() => setShowModal(false)}
                      className="flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap"
                      style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.12)" }}
                    >
                      İptal
                    </button>
                    <button
                      onClick={() => { if (form.name && form.service) setSaved(true); }}
                      className="flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap"
                      style={{ background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff" }}
                    >
                      <i className="ri-save-line mr-1.5" />
                      Müşteriyi Kaydet
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
